import Shell from '../components/Shell';
import { C2Button, C2Card, C2GoTag, C2Section } from '../components/c2';
import type { DecorShape } from '../components/c2/Section';
import type { PastelColor } from '../components/c2/Pill';
import PageHero from '../components/secondary/PageHero';
import BottomCTA from '../components/secondary/BottomCTA';

const shortVersionDecor: DecorShape[] = [
  { shape: 'circle', size: 220, color: 'sky', style: { position: 'absolute', top: 40, left: -90, opacity: 0.45 } },
  { shape: 'pill', size: 220, color: 'lemon', style: { position: 'absolute', top: 180, right: -70, opacity: 0.45, transform: 'rotate(8deg)' } },
  { shape: 'square', size: 130, color: 'mint', style: { position: 'absolute', bottom: 40, left: '15%', opacity: 0.45, transform: 'rotate(-10deg)' } },
];

const principlesDecor: DecorShape[] = [
  { shape: 'circle', size: 240, color: 'peach', style: { position: 'absolute', top: 60, left: -100, opacity: 0.45 } },
  { shape: 'pill', size: 220, color: 'lilac', style: { position: 'absolute', top: 240, right: -80, opacity: 0.45, transform: 'rotate(-8deg)' } },
  { shape: 'square', size: 140, color: 'sky', style: { position: 'absolute', bottom: 60, left: '12%', opacity: 0.45, transform: 'rotate(10deg)' } },
  { shape: 'circle', size: 180, color: 'mint', style: { position: 'absolute', bottom: 40, right: -50, opacity: 0.45 } },
];

const fccDecor: DecorShape[] = [
  { shape: 'circle', size: 180, color: 'lemon', style: { position: 'absolute', top: -40, left: '10%', opacity: 0.45 } },
  { shape: 'pill', size: 200, color: 'peach', style: { position: 'absolute', bottom: -30, right: '15%', opacity: 0.45, transform: 'rotate(-8deg)' } },
];

const pastelBg: Record<PastelColor, string> = {
  peach: 'var(--color-peach)',
  mint: 'var(--color-mint)',
  sky: 'var(--color-sky)',
  lemon: 'var(--color-lemon)',
  lilac: 'var(--color-lilac)',
};

interface Principle { emoji: string; title: string; body: string; color: PastelColor }

const principles: Principle[] = [
  { emoji: '🪴', title: 'Grown, not scaled', body: "We're a nonprofit, not a startup. We don't need hockey-stick growth — we just need this thing to keep working for teams who use it.", color: 'mint' },
  { emoji: '🧾', title: 'No paywalls, ever', body: 'No features sit behind paywalls. If it\'s useful, everyone gets it.', color: 'peach' },
  { emoji: '🔍', title: 'Radical transparency', body: 'All our code, our roadmap, and our uptime are public. What you see is what we are.', color: 'sky' },
  { emoji: '🤝', title: 'Humans answer', body: 'When you email us, a real person replies. Usually the one who wrote the feature you\'re asking about.', color: 'lilac' },
];

export default function AboutPage() {
  return (
    <Shell>
      <PageHero
        title="Built by people who missed go/ links"
        titleAccentWord="missed"
        decorShapes={[
          { shape: 'circle', size: 300, color: 'peach', style: { position: 'absolute', top: -40, left: -100, opacity: 0.5 } },
          { shape: 'circle', size: 220, color: 'mint', style: { position: 'absolute', top: 40, right: -60, opacity: 0.5 } },
          { shape: 'pill', size: 220, color: 'lilac', style: { position: 'absolute', bottom: 0, right: '20%', opacity: 0.5, transform: 'rotate(-12deg)' } },
        ]}
      />

      <C2Section pad={48} decor={shortVersionDecor}>
        <div className="max-w-[720px] mx-auto">
          <div
            className="font-mono uppercase mb-5"
            style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-ink-muted)', letterSpacing: '0.1em' }}
          >
            The short version
          </div>
          <div
            className="font-display text-ink"
            style={{ fontSize: 28, lineHeight: 1.45, fontWeight: 500, letterSpacing: '-0.01em' }}
          >
            <p className="m-0 mb-6">
              At Google, <em>everyone</em> had go/ links. You'd type <C2GoTag slug="perf" size="lg" /> and land on the performance dashboard. <C2GoTag slug="plx" size="lg" /> opened the query tool. Nobody remembered URLs.
            </p>
            <p className="m-0 mb-6">
              Then we left. And at every job after we missed our go/ links.
            </p>
            <p className="m-0">
              So, we built this.
            </p>
          </div>
        </div>
      </C2Section>

      <C2Section pad={80} bg="var(--color-surface)" decor={principlesDecor}>
        <div className="text-center mb-14">
          <h2
            className="font-display font-extrabold text-ink m-0"
            style={{ fontSize: 44, letterSpacing: '-0.02em' }}
          >
            What we <span className="italic" style={{ color: 'var(--accent-deep)' }}>believe</span>
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-6 max-w-[960px] mx-auto">
          {principles.map(p => (
            <C2Card key={p.title} pad={32} className="flex gap-5 items-start">
              <div
                className="w-[52px] h-[52px] rounded-md flex items-center justify-center text-[26px] shrink-0"
                style={{ background: pastelBg[p.color] }}
              >
                {p.emoji}
              </div>
              <div>
                <h3 className="font-display font-bold text-ink m-0 mb-2" style={{ fontSize: 22, letterSpacing: '-0.01em' }}>
                  {p.title}
                </h3>
                <p className="text-[15px] text-ink-soft m-0 leading-[1.6]">{p.body}</p>
              </div>
            </C2Card>
          ))}
        </div>
      </C2Section>

      <C2Section pad={40} decor={fccDecor}>
        <div
          className="rounded-xl grid grid-cols-[1fr_auto] gap-8 items-center max-w-[960px] mx-auto"
          style={{
            background: 'var(--accent-tint)',
            border: '1px solid var(--accent-soft)',
            padding: '40px clamp(24px, 4vw, 48px)',
          }}
        >
          <div>
            <div
              className="font-mono uppercase mb-2.5"
              style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent-deep)', letterSpacing: '0.1em' }}
            >
              About our umbrella
            </div>
            <h3 className="font-display font-bold text-ink m-0" style={{ fontSize: 28, letterSpacing: '-0.01em' }}>
              Free Code Collective
            </h3>
            <p className="text-[15px] text-ink-soft mt-2.5 leading-[1.6] m-0">
              A not-for-profit that builds open tools that teams love. Wanna help? Tell us what you're building.
            </p>
          </div>
          <a href="https://freecodecollective.org" target="_blank" rel="noopener noreferrer">
            <C2Button variant="outline" size="lg">Visit FCC →</C2Button>
          </a>
        </div>
      </C2Section>

      <BottomCTA
        title="Want to help?"
        titleAccent="help"
        sub="Contribute on GitHub, file an issue, or just tell a coworker. It all helps."
      />
    </Shell>
  );
}
